import React from 'react';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import { VseApplicationEntity, MajorEntity, StudyLevelEntity, StudyYearEntity, UniversityEntity, ApplStatusEntity, ClassEntity, CourseEntity, TeachingModeEntity, SeasonEntity, TimeZoneEntity } from '../types';
import { VseApplFormPersonalParticularsReadyOnly } from './VseApplFormPersonalParticularsReadyOnly';
import { VseApplFormApplicationStatus } from './VseApplFormApplicationStatus';
import { VseApplFormApplClass } from './VseApplFormApplClass';

const useStyles = makeStyles((theme: Theme) => ({
  backButton: {
    marginRight: theme.spacing(1),
  },
  buttonsTop: {
    backgroundColor: 'inherit',
  },
  form: {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  paper: {
    margin: 'auto',
    overflow: 'hidden',
    backgroundColor: theme.palette.action.disabledBackground,
  },
  approvalSection: {
    backgroundColor: theme.palette.common.white,
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(0),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1.5),
    height: '100%',
  },
  infoSection: {
    paddingTop: theme.spacing(2.5),
    paddingBottom: theme.spacing(0),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1.5),
    height: '100%',
  },
  classSection: {
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(0),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1.5),
    height: '100%',
  },
  secondaryBar: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
  },
  sectionTitle: {
    marginLeft: theme.spacing(-1),
    paddingBottom: theme.spacing(1.5),
  },
  uphead: {
    textTransform: "uppercase",
  },
  root: {
    height: '100%',
  },
  stepCaption: {
    fontSize: '0.7rem',
  },
  stepInstructions: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  hidden: {
    display: 'none',
  },
  headingContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(2),
  },
}));

export interface VseApplFormProps {
  application: VseApplicationEntity,
  classesOfApplicationSelected?: ClassEntity[],
  coursesOfApplicationSelected?: CourseEntity[],
  majorOptions: MajorEntity[],
  studyLevelOptions: StudyLevelEntity[],
  studyYearOptions: StudyYearEntity[],
  universityOptions: UniversityEntity[],
  seasonOptions: SeasonEntity[],
  applStatusOptions: ApplStatusEntity[],
  teachingModeOptions: TeachingModeEntity[],
  timeZoneOptions: TimeZoneEntity[],
  onChange: (changes: Pick<Partial<VseApplicationEntity>,
    'submissionDate' |
    'endorsedDate' |
    'approvalDate' |
    'withdrawalDate' |
    'adminRemark' |
    'status'>) => void,
};

export function VseApplForm(props: VseApplFormProps) {
  const theme = useTheme();
  const classes = useStyles(theme);

  function getUniversityEntity(id: number): UniversityEntity | undefined {
    return props.universityOptions.find((e) => e.universityID === id);
  }

  function getCourse(code: string): CourseEntity | undefined {
    return props.coursesOfApplicationSelected?.find((e) => e.courseCode === code);
  }

  const displayContent = () => {
    return <VseApplFormPersonalParticularsReadyOnly
      data={props.application}
      majorOptions={props.majorOptions}
      studyLevelOptions={props.studyLevelOptions}
      studyYearOptions={props.studyYearOptions}
      universityOptions={props.universityOptions}
    />
  }

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <form className={classes.form}>
          <div className={classes.approvalSection}>
            <div className={classes.sectionTitle}>
              <Grid item xs={12} md={12} component={Typography} variant='h6'>Application Status</Grid>
            </div>
            <VseApplFormApplicationStatus
              data={props.application}
              applStatusOptions={props.applStatusOptions}
              onChange={(changes) => {
                props.onChange(changes);
              }}
            />
          </div>
          <div className={classes.infoSection}>
            <div className={classes.sectionTitle}>
              <Grid item xs={12} md={12} component={Typography} variant='h6'>Personal Particulars</Grid>
            </div>
            {displayContent()}
          </div>
          <div className={classes.classSection}>
            <div className={classes.sectionTitle}>
              <Grid item xs={12} md={12} component={Typography} variant='h6'>Class Selection</Grid>
            </div>
            <VseApplFormApplClass
              data={props.classesOfApplicationSelected}
              teachingModeOptions={props.teachingModeOptions}
              timeZoneOptions={props.timeZoneOptions}
              getUniversity={getUniversityEntity}
              getCourse={getCourse}
            />
          </div>
        </form>
      </Paper>
    </div>
  );
}
